import React, { useState, useLayoutEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { LogIn, Loader2, AlertCircle, CheckCircle2, Shield, Truck, BarChart3, Clock, FileSearch, Zap, ArrowRight, Mail, Copy, Eye, EyeOff } from 'lucide-react';
import { fetchFreeTrial } from '../lib/api';

const TOKEN_KEY = 'tachoparser_token';
const USER_KEY = 'tachoparser_user';

/**
 * FORCED-DARK CHROME (DDIR theme spec v1 §2.4).
 *
 * The login screen is drawn dark on purpose — the backdrop, the card, the gold
 * ring and the feature strip are a single composed image, and its colours are
 * hardcoded rather than tokenised for exactly that reason.
 *
 * Light mode would half-apply to it anyway: `html.light input` is (0,1,1) and
 * beats `.login-input` at (0,1,0), so the fields would go white inside a dark
 * card, and the page ground behind would turn ivory.
 *
 * So the class comes OFF while this screen is mounted and goes back ON when it
 * unmounts. The STORED PREFERENCE IS NEVER TOUCHED: a light-mode user who signs
 * out sees a dark login screen and lands back in light when they sign in.
 *
 * useLayoutEffect, not useEffect: an effect runs AFTER paint, which would show
 * one frame of a half-light login screen on every sign-out. A layout effect
 * runs before the browser paints, so there is nothing to see.
 */
function useForcedDarkChrome() {
  useLayoutEffect(() => {
    const r = document.documentElement.classList;
    const wasLight = r.contains('light');
    if (!wasLight) return undefined;
    r.remove('light');
    r.add('dark');
    return () => {
      r.add('light');
      r.remove('dark');
    };
  }, []);
}

export default function LoginPage() {
  useForcedDarkChrome();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showFreeTrial, setShowFreeTrial] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [trialForm, setTrialForm] = useState({ name: '', email: '', company: '', fleet_size: '' });
  const [trialSubmitted, setTrialSubmitted] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState('');
  const [trialPassword, setTrialPassword] = useState('');
  const [showTrialPassword, setShowTrialPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0c0f14',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        display: 'flex',
        maxWidth: 1000,
        width: '100%',
        gap: 0,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
      }}>
        {/* Left Panel — Branding & Features */}
        <div style={{
          flex: 1,
          background: '#141922',
          padding: '48px 40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRight: '1px solid rgba(255,255,255,0.05)',
          minWidth: 0,
        }}>
          {/* Brand Name */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 28,
              color: '#ffffff',
              margin: 0,
              lineHeight: 1.1,
            }}>
              Tacho Analytics
            </h1>
            <p style={{ color: 'rgba(169, 177, 189, 0.8)', fontSize: 12, margin: '4px 0 0', letterSpacing: '0.05em' }}>
              TACHOGRAPH COMPLIANCE PLATFORM
            </p>
          </div>

          {/* Tagline */}
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            color: '#eef0f3',
            margin: '0 0 8px',
            lineHeight: 1.3,
          }}>
            Automated Tachograph<br />Analysis Platform
          </h2>
          <p style={{
            color: 'rgba(169, 177, 189, 0.7)',
            fontSize: 14,
            lineHeight: 1.6,
            margin: '0 0 28px',
          }}>
            Automated driving hours and working hours analysis for HGV and PSV fleets.
            Upload driver cards and vehicle unit data — get instant insights.
          </p>

          {/* Pricing */}
          <div style={{
            background: 'rgba(60, 179, 113,0.1)',
            border: '1px solid rgba(60, 179, 113,0.25)',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 24,
          }}>
            <span style={{ color: '#9ad9b5', fontSize: 14, fontWeight: 600 }}>
              Simple transparent pricing
            </span>
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <span style={{ color: '#eef0f3', fontSize: 13 }}>
                <strong style={{ color: '#6cc793', fontSize: 18 }}>£1</strong>/driver/week
              </span>
              <span style={{ color: 'rgba(169, 177, 189, 0.4)' }}>|</span>
              <span style={{ color: '#eef0f3', fontSize: 13 }}>
                <strong style={{ color: '#6cc793', fontSize: 18 }}>50p</strong>/vehicle/week
              </span>
            </div>
            <p style={{ color: 'rgba(169, 177, 189, 0.6)', fontSize: 11, margin: '6px 0 0' }}>
              Pay As You Use — No contracts, no setup fees
            </p>
          </div>

          {/* Features */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {[
              { icon: CheckCircle2, text: 'Free no-obligation trial', color: '#6cc793' },
              { icon: BarChart3, text: 'EU 561 & Working Time Directive analysis', color: '#93aed4' },
              { icon: Truck, text: 'Driver & vehicle compliance scoring', color: '#e5a33b' },
              { icon: FileSearch, text: 'Missing mileage & driving without card detection', color: '#ee7f76' },
              { icon: Zap, text: 'Speed analysis & harsh braking monitoring', color: '#facc15' },
              { icon: Clock, text: 'Real-time calendar with deep-dive activity log', color: '#6cc793' },
              { icon: Shield, text: 'ISO 27001 accredited security', color: '#93aed4' },
            ].map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <f.icon size={16} stroke={f.color} />
                <span style={{ color: '#a9b1bd', fontSize: 13 }}>{f.text}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16, marginTop: 'auto' }}>
            <p style={{ color: 'rgba(169, 177, 189, 0.5)', fontSize: 11, margin: 0, lineHeight: 1.6 }}>
              Powered by <strong style={{ color: 'rgba(169, 177, 189, 0.7)' }}>Fleet TranspoRT Consultants Ltd</strong>
            </p>
            <p style={{ color: 'rgba(169, 177, 189, 0.4)', fontSize: 10, margin: '2px 0 0' }}>
              Part of the DDIR Family of Transport Compliance Apps
            </p>
            <p style={{ color: 'rgba(169, 177, 189, 0.35)', fontSize: 10, margin: '2px 0 0' }}>
              ISO 27001 Accredited Information Security Management
            </p>
          </div>
        </div>

        {/* Right Panel — Login Form */}
        <div style={{
          width: 380,
          background: 'rgba(20, 25, 34, 0.95)',
          padding: '48px 36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
            <img src="/logo.png" alt="Tacho Analytics" style={{ width: 120, height: 120, borderRadius: 16, marginBottom: 12 }} />
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 20,
              fontWeight: 400,
              color: '#ffffff',
              margin: 0,
              textAlign: 'center',
            }}>
              Secure Access
            </h2>
            <p style={{ color: 'rgba(169, 177, 189, 0.5)', fontSize: 12, margin: '4px 0 0', textAlign: 'center' }}>
              Sign in to your compliance dashboard
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: 'rgba(232, 89, 78, 0.08)',
              border: '1px solid rgba(232, 89, 78, 0.2)',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13,
              color: '#e8594e',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{
                fontSize: 12, fontWeight: 600, color: '#7b8593',
                
              }}>
                Email Address <span style={{ color: '#e8594e' }}>*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="Enter your email"
                style={{
                  background: 'rgba(27, 34, 46, 0.5)',
                  border: '1px solid rgba(120, 130, 145, 0.4)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  color: '#eef0f3',
                  fontSize: 14,
                  fontFamily: 'var(--font-body)',
                  outline: 'none',
                  transition: 'border-color 200ms',
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(60, 179, 113, 0.5)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(120, 130, 145, 0.4)'}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{
                fontSize: 12, fontWeight: 600, color: '#7b8593',
                
              }}>
                Password <span style={{ color: '#e8594e' }}>*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                style={{
                  background: 'rgba(27, 34, 46, 0.5)',
                  border: '1px solid rgba(120, 130, 145, 0.4)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  color: '#eef0f3',
                  fontSize: 14,
                  fontFamily: 'var(--font-body)',
                  outline: 'none',
                  transition: 'border-color 200ms',
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(60, 179, 113, 0.5)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(120, 130, 145, 0.4)'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: 46,
                background: loading ? 'rgba(60, 179, 113, 0.5)' : '#3cb371',
                border: 'none',
                borderRadius: 8,
                color: '#0c0f14',
                fontSize: 15,
                fontWeight: 700,
                fontFamily: 'var(--font-body)',
                cursor: loading ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 4,
                transition: 'all 200ms',
                boxShadow: 'none',
              }}
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <LogIn size={18} />
              )}
              {loading ? 'Signing in...' : 'LOGIN'}
            </button>
          </form>

          {/* Free trial link */}
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <a href="#" onClick={(e) => { e.preventDefault(); setShowFreeTrial(true); }} style={{
              color: '#3cb371',
              fontSize: 13,
              textDecoration: 'none',
              fontWeight: 500,
            }}>
              Start Free Trial →
            </a>
          </div>

          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <a href="#" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }} style={{
              color: 'rgba(169, 177, 189, 0.5)',
              fontSize: 12,
              textDecoration: 'none',
            }}>
              Forgot Password?
            </a>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => { setShowForgotPassword(false); setResetSent(false); }}>
          <div style={{
            background: '#141922', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14,
            padding: '32px', width: 380, maxWidth: '90vw',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#ffffff', margin: '0 0 8px', fontFamily: 'var(--font-body)', fontSize: 18 }}>
              Reset Password
            </h3>
            {resetSent ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <CheckCircle2 size={48} stroke="#6cc793" style={{ marginBottom: 12 }} />
                <p style={{ color: '#a9b1bd', fontSize: 14 }}>Password reset link sent to <strong>{resetEmail}</strong></p>
                <p style={{ color: 'rgba(169, 177, 189, 0.5)', fontSize: 12 }}>Check your inbox and spam folder</p>
              </div>
            ) : (
              <>
                <p style={{ color: 'rgba(169, 177, 189, 0.6)', fontSize: 13, margin: '0 0 20px' }}>
                  Enter your email and we'll send you a reset link.
                </p>
                <input
                  type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{
                    width: '100%', background: 'rgba(27, 34, 46, 0.5)', border: '1px solid rgba(120, 130, 145, 0.4)',
                    borderRadius: 8, padding: '12px 14px', color: '#eef0f3', fontSize: 14,
                    fontFamily: 'var(--font-body)', outline: 'none', marginBottom: 16, boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={() => { if (resetEmail) setResetSent(true); }}
                  style={{
                    width: '100%', height: 42, background: '#3cb371',
                    border: 'none', borderRadius: 8, color: '#0c0f14', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <Mail size={16} /> Send Reset Link
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Free Trial Modal */}
      {showFreeTrial && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => { if (!trialSubmitted) { setShowFreeTrial(false); setTrialError(''); } }}>
          <div style={{
            background: '#141922', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14,
            padding: '32px', width: 420, maxWidth: '90vw',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#ffffff', margin: '0 0 8px', fontFamily: 'var(--font-body)', fontSize: 18 }}>
              Start Your Free Trial
            </h3>
            {trialSubmitted ? (
              <div style={{ padding: '16px 0' }}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <CheckCircle2 size={48} stroke="#6cc793" style={{ marginBottom: 12 }} />
                  <p style={{ color: '#a9b1bd', fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>
                    Welcome, {trialForm.name}!
                  </p>
                  <p style={{ color: 'rgba(169, 177, 189, 0.6)', fontSize: 13, margin: 0 }}>
                    Your 14-day free trial is ready. Save your password below.
                  </p>
                </div>

                {/* Password display */}
                <div style={{
                  background: 'rgba(27, 34, 46, 0.6)', border: '1px solid rgba(108, 199, 147, 0.3)',
                  borderRadius: 10, padding: '14px 16px', marginBottom: 16,
                }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#7b8593', display: 'block', marginBottom: 8 }}>
                    Your Password
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{
                      flex: 1, background: 'rgba(20, 25, 34, 0.8)', border: '1px solid rgba(120, 130, 145, 0.5)',
                      borderRadius: 6, padding: '10px 12px', color: '#6cc793', fontSize: 16,
                      fontFamily: 'var(--font-mono)', letterSpacing: '0.05em',
                    }}>
                      {showTrialPassword ? trialPassword : '\u2022'.repeat(trialPassword.length)}
                    </code>
                    <button
                      onClick={() => setShowTrialPassword(!showTrialPassword)}
                      title={showTrialPassword ? 'Hide' : 'Show'}
                      style={{
                        background: 'rgba(120, 130, 145, 0.4)', border: '1px solid rgba(120, 130, 145, 0.6)',
                        borderRadius: 6, padding: '8px', cursor: 'pointer', color: '#7b8593',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      {showTrialPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(trialPassword);
                        setPasswordCopied(true);
                        setTimeout(() => setPasswordCopied(false), 2000);
                      }}
                      title="Copy password"
                      style={{
                        background: passwordCopied ? 'rgba(108, 199, 147, 0.2)' : 'rgba(120, 130, 145, 0.4)',
                        border: `1px solid ${passwordCopied ? 'rgba(108, 199, 147, 0.4)' : 'rgba(120, 130, 145, 0.6)'}`,
                        borderRadius: 6, padding: '8px', cursor: 'pointer',
                        color: passwordCopied ? '#6cc793' : '#7b8593',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      {passwordCopied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                  <p style={{ color: 'rgba(238, 127, 118, 0.8)', fontSize: 11, margin: '8px 0 0', fontWeight: 500 }}>
                    Save this password now -- you will need it to log in again.
                  </p>
                </div>

                <div style={{
                  background: 'rgba(27, 34, 46, 0.4)', borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                  fontSize: 12, color: 'rgba(169, 177, 189, 0.6)',
                }}>
                  <strong style={{ color: '#7b8593' }}>Email:</strong> {trialForm.email}
                </div>

                <button
                  onClick={() => {
                    // Auto-login: store token and user, then reload to enter the app
                    window.location.reload();
                  }}
                  style={{
                    width: '100%', height: 44, background: '#3cb371',
                    border: 'none', borderRadius: 8, color: '#0c0f14', fontSize: 15, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: 'none',
                  }}
                >
                  <ArrowRight size={16} /> Enter Dashboard
                </button>
              </div>
            ) : (
              <>
                <p style={{ color: 'rgba(169, 177, 189, 0.6)', fontSize: 13, margin: '0 0 20px' }}>
                  No obligation, no credit card required. Full access for 14 days.
                </p>

                {/* Trial error */}
                {trialError && (
                  <div style={{
                    background: 'rgba(232, 89, 78, 0.08)', border: '1px solid rgba(232, 89, 78, 0.2)',
                    borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#e8594e',
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                  }}>
                    <AlertCircle size={16} />
                    {trialError}
                  </div>
                )}

                {[
                  { key: 'name', label: 'Your Name', placeholder: 'John Smith', type: 'text' },
                  { key: 'email', label: 'Email Address', placeholder: 'john@company.com', type: 'email' },
                  { key: 'company', label: 'Company Name', placeholder: 'Your Transport Ltd', type: 'text' },
                  { key: 'fleet_size', label: 'Fleet Size (approx.)', placeholder: 'e.g. 12 vehicles', type: 'text' },
                ].map((field) => (
                  <div key={field.key} style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#7b8593' }}>
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      value={trialForm[field.key]}
                      onChange={(e) => setTrialForm({ ...trialForm, [field.key]: e.target.value })}
                      placeholder={field.placeholder}
                      style={{
                        width: '100%', background: 'rgba(27, 34, 46, 0.5)', border: '1px solid rgba(120, 130, 145, 0.4)',
                        borderRadius: 8, padding: '10px 14px', color: '#eef0f3', fontSize: 13,
                        fontFamily: 'var(--font-body)', outline: 'none', marginTop: 4, boxSizing: 'border-box',
                      }}
                    />
                  </div>
                ))}
                <button
                  disabled={trialLoading}
                  onClick={async () => {
                    if (!trialForm.name || !trialForm.email || !trialForm.company) {
                      setTrialError('Please fill in your name, email, and company.');
                      return;
                    }
                    setTrialError('');
                    setTrialLoading(true);
                    try {
                      const data = await fetchFreeTrial({
                        name: trialForm.name.trim(),
                        email: trialForm.email.trim(),
                        company: trialForm.company.trim(),
                        fleet_size: trialForm.fleet_size.trim(),
                      });
                      // Store token + user for immediate login
                      localStorage.setItem(TOKEN_KEY, data.token);
                      localStorage.setItem(USER_KEY, JSON.stringify({
                        id: data.user.id,
                        email: data.user.email,
                        display_name: data.user.display_name,
                        is_super_admin: data.user.is_super_admin,
                        org_id: data.user.org_id,
                        org_name: data.user.org_name,
                        role: data.user.role,
                        memberships: [],
                      }));
                      setTrialPassword(data.password);
                      setTrialSubmitted(true);
                    } catch (err) {
                      setTrialError(err.message || 'Failed to create trial account');
                    } finally {
                      setTrialLoading(false);
                    }
                  }}
                  style={{
                    width: '100%', height: 42,
                    background: trialLoading ? 'rgba(60, 179, 113, 0.5)' : '#3cb371',
                    border: 'none', borderRadius: 8, color: '#0c0f14', fontSize: 14, fontWeight: 700,
                    cursor: trialLoading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    marginTop: 4, boxShadow: 'none',
                  }}
                >
                  {trialLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> Creating account...</>
                  ) : (
                    <><ArrowRight size={16} /> Start Free Trial</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
